<template>
  <v-flex xs12>
    <v-card flat>
      <v-card-title>
        <span class="subtitle-1">Call sheets</span>
        <v-spacer></v-spacer>
        <tool-chip-count
          class="mr-1"
          :count="callsheetsCount"
          :tooltip="`${callsheetsCount} callsheets uploaded`"
          left
        ></tool-chip-count>
        <hide-detail
          prop-name="callsheetsection"
          tooltip="uploaded callsheets"
          left
          :hide="!showCallsheets"
          @hide="hideSection"
        ></hide-detail>
      </v-card-title>

      <v-card flat v-if="showCallsheetSub" ref="callsheettable">
        <v-card flat v-if="showItem===-1">
          <v-card-text>
            <v-data-table
              dense
              :headers="callsheetHeaders"
              :items="callsheetData"
              hide-default-footer
              no-data-text="no callsheets have been loaded yet for this film"
              :items-per-page="(callsheetData && callsheetData.length) || 10"
            >
              <template v-if="committing" v-slot:progress>
                <v-progress-linear :active="committing" indeterminate height="2"></v-progress-linear>
              </template>

              <template v-slot:header.status>
                <v-icon small :color="getIconColor('cloudactivity')">{{getIcon("cloudactivity")}}</v-icon>
              </template>
              <template v-slot:item="{item}">
                <callsheet-row
                  :item="item"
                  :committing="committing"
                  @showanalyses="showAnalyses"
                  @edit="editItem"
                  @delete="deleteItem"
                  @rerun="rerunItem"
                  @refetch="refetch"
                ></callsheet-row>
              </template>
              <template v-slot:top>
                <v-dialog v-model="dialog">
                  <v-card flat>
                    <v-card-title>
                      <span class="headline">Edit callsheet</span>
                    </v-card-title>
                    <v-card-text>
                      <v-container>
                        <v-row>
                          <v-col cols="12" sm="6" md="4">
                            <v-text-field
                              v-model="updatedItem.comment"
                              label="comment"
                              @input="saveDisabled=false"
                            ></v-text-field>
                          </v-col>
                        </v-row>
                      </v-container>
                    </v-card-text>
                    <v-card-actions>
                      <div class="flex-grow-1"></div>
                      <v-btn color="primary" @click="save" :disabled="saveDisabled">Save</v-btn>
                      <v-btn @click="close">Cancel</v-btn>
                    </v-card-actions>
                  </v-card>
                </v-dialog>
              </template>
            </v-data-table>
          </v-card-text>
          <v-card-text>
            <v-container>
              <v-progress-linear v-if="committing" :active="committing" indeterminate height="2"></v-progress-linear>
            </v-container>
          </v-card-text>
          <v-card-text>
            <v-fab-transition>
              <v-btn
                absolute
                bottom
                right
                fab
                small
                @click="uploading=true"
                :disabled="uploading"
                color="primary"
              >
                <v-icon>add</v-icon>
              </v-btn>
            </v-fab-transition>
          </v-card-text>
        </v-card>
        <v-card flat v-else>
          <callsheet-analyses
            v-if="findData(showItem)"
            :callsheet="findData(showItem)"
            :reason="reason"
            @close="closeCallsheetAnalyses"
          ></callsheet-analyses>
        </v-card>
      </v-card>

      <v-card flat v-if="uploading">
        <v-card-text>
          <callsheet-upload
            :run-id="runID"
            :artwork-type-id="callSheetArtworkTypeID"
            :artwork-comment="`callsheet for film ${filmID} - ${filmName}`"
            :film-id="filmID"
            @error="uploadError"
            @done="uploadDone"
            @cancel="uploadCancel"
            :master="pickMaster"
          ></callsheet-upload>
        </v-card-text>
      </v-card>
    </v-card>
  </v-flex>
</template>
<script>
/**
 * this issues the filmtocallsheets query
 * and gets every call sheet associated with a film
 * and creates a table showing them
 * the content of the table is taken care of by callsheetrow
 * when it receives a showanalyses event from callsheet row
 * it shows the callsheetanalyses component
 */
import Hidedetail from "@/components/Hidedetail";
import Callsheetupload from "@/components/Callsheetupload";
import Callsheetanalyses from "@/components/Callsheetanalyses";
import Callsheetrow from "@/components/Callsheetrow";
import Toolchipcount from "@/components/Toolchipcount";
import { FilmToCallsheets, provokeCallsheetAnalysis } from "@/gql";
import { getIconColor, getIcon, settings } from "@/js/store";
import { mapState, mapMutations } from "vuex";
import { vxMaps } from "@/js/vxstore";
import {
  updateCallsheetGql,
  removeCallsheetAndArtworkGql
} from "@/js/mutations";
import { showToaster, mutate } from "@/js/apollo";
import { SearchUtils } from "@/js";

const defaultItem = {
  comment: "",
  id: -1
};
export default {
  components: {
    "callsheet-upload": Callsheetupload,
    "tool-chip-count": Toolchipcount,
    "callsheet-row": Callsheetrow,
    "callsheet-analyses": Callsheetanalyses,
    "hide-detail": Hidedetail
  },
  props: {
    reason: Object,
    editing: String,
    editable: Boolean,
    selectedMaster: Object
  },
  watch: {
    dialog(val) {
      val || this.close();
    },
    updatedItem(val) {
      if (this.callsheetData && val && val.id !== -1) {
        const current = this.findData(val.id);
        this.saveDisabled = !current || val.comment === current.comment;
      } else {
        this.saveDisabled = true;
      }
    }
  },
  methods: {
    closeCallsheetAnalyses() {
      this.showItem = -1;
      // this will be used to reset the screen back to its original state before showing this
      this.$vuetify.goTo(this.scrollPosition);
    },
    hideSection(pack) {
      this.setBigActiveSection (pack.hide ? null : pack.propName)
      this.hideSub(pack)
    },
    hideSub(pack) {
      this.setVActiveSub (pack.hide ? null : pack.propName)
    },
    showAnalyses({ item }) {
      this.showItem = item.id;
      this.scrollPosition = window.pageYOffset;
    },
    getIcon(name) {
      return getIcon(name);
    },
    getIconColor(name) {
      return getIconColor(name);
    },
    findData(id) {
      return this.callsheetData.find(f => f.id === id);
    },
    flipShowItem(item) {
      this.showItem = this.showItem === item.id ? -1 : item.id;
    },
    getArtworkLink(item) {
      return SearchUtils.getArtworkLink({ base: item });
    },
    save() {
      this.committing = true;
      updateCallsheetGql({
        callsheet: this.updatedItem,
        apolloClient: this.$apollo
      })
        .then(() => this.close())
        .catch(error => {
          this.committing = false;
          showToaster({
            header: "graphql failure",
            type: "error",
            message: "failed to update callsheet",
            error
          });
        });
    },
    editItem({ item }) {
      this.updatedItem = Object.assign({}, item);
      this.dialog = true;
    },
    provokeAnalysis({ item }) {
      return mutate({
        mutation: provokeCallsheetAnalysis,
        variables: {
          input: {
            callsheetID: item.id
          }
        }
      });
    },
    rerunItem({ item }) {
      const confirmed = confirm(
        "Are you sure you want to re analyze this callsheet and remove current assignments ?"
      );
      if (confirmed) {
        this.provokeAnalysis({ item }).catch(error => {
          this.committing = false;
          showToaster({
            header: "graphql failure",
            type: "error",
            message: "failed to provoke call sheet",
            error
          });
        });
      }
    },
    deleteItem({ item }) {
      const confirmed = confirm(
        "Are you sure you want to delete this callsheet and all associated analyses ?"
      );
      if (confirmed) {
        this.committing = true;
        removeCallsheetAndArtworkGql({
          callsheet: item,
          apolloClient: this.$apollo
        })
          .then(() => this.close())
          .then(() => this.uploadDone())
          .catch(error => {
            this.committing = false;
            showToaster({
              header: "graphql failure",
              type: "error",
              message: "failed to remove call sheet",
              error
            });
          });
      }
    },
    refetch() {
      return this.$apollo.queries.FilmToCallsheets.refetch();
    },
    uploadDone() {
      // now create call sheet(s)
      this.committing = true;
      this.refetch().then(() => (this.committing = false));
      // also need to clear out the uploadable files
      this.setThumbData([]);
      this.uploading = false;
    },
    uploadError({ error }) {
      console.log("failed upload", error);
    },
    uploadCancel() {
      this.uploading = false;
    },
    close() {
      this.dialog = false;
      this.saveDisabled = true;
      this.updatedItem = Object.assign({}, defaultItem);
      this.committing = false;
    },
    ...mapMutations(vxMaps.mutations)
  },
  data: () => ({
    scrollPosition: 0,
    fab: false,
    committing: false,
    uploading: false,
    FilmToCallsheets: null,
    runID: SearchUtils.getRunID(),
    dialog: false,
    updatedItem: defaultItem,
    saveDisabled: true,
    showItem: -1
  }),
  computed: {
    pickMaster () {
      const s = (this.selectedMaster && this.selectedMaster.master) || this.filmMaster
      return s;
    },
    showCallsheets() {
      return  this.bigActiveSection === "callsheetsection";
    },
    showCallsheetSub() {
      return this.showCallsheets && this.vActiveSub === 'callsheetsection'
    },
    defaultItem() {
      return {
        comment: "",
        id: -1
      };
    },
    callsheets() {
      // callsheets are now held at filmmaster level - so concatenate them
      const m =  this.FilmToCallsheets && this.FilmToCallsheets.FilmMasters;
      return m && m.reduce ((p,c)=> {
        return p.concat(c.Callsheets)
      },[])

    },
    callsheetData() {
      // sorting on runid will move the latest to the top
      return (this.callsheets || [])
        .slice()
        .sort((a, b) => (a.runID > b.runID ? -1 : a.runID === b.runID ? 0 : 1))
        .map(f => {
          const d = SearchUtils.getDateFromRunID({ runID: f.runID });
          f.uploaded = d ? d.toLocaleDateString() : "-";
          return f;
        });
    },
    callsheetsCount() {
      return this.callsheets && this.callsheets.length;
    },
    filmToCallsheetsVariables() {
      return {
        id: this.filmID
      };
    },
    callSheetArtworkTypeID() {
      return settings.dbStaticTypes.artworkTypes.callsheet;
    },
    callsheetHeaders() {
      return [
        {
          text: "Status",
          value: "status",
          width: "6%",
          align: "center",
          sortable: false
        },
        {
          text: "Name",
          value: "originalName",
          width: "17%",
          align: "left",
          sortable: false
        },
        {
          text: "Comment",
          value: "comment",
          width: "20%",
          align: "left",
          sortable: false
        },
        {
          text: "Uploaded",
          value: "uploaded",
          width: "17%",
          align: "left",
          sortable: false
        },
        {
          text: "Action",
          value: "action",
          width: "40%",
          align: "center",
          sortable: false
        }
      ];
    },
    labelColor() {
      return getIconColor("labels");
    },
    xs() {
      return this.$vuetify.breakpoint.xs;
    },
    film() {
      return this.reason && this.reason.Film;
    },
    filmID() {
      return this.film && this.film.id;
    },
    filmName() {
      return this.film && this.film.filmName;
    },
    filmMasters() {
      return this.film && this.film.FilmMasters;
    },
    filmMaster() {
      return this.filmMasters && this.filmMasters[0];
    },
    filmMasterID() {
      return this.filmMaster && this.filmMaster.id;
    },
    errorIcon() {
      return getIcon("error");
    },
    ...mapState(vxMaps.state)
  },
  apollo: {
    FilmToCallsheets: {
      skip() {
        return !this.filmID ;
      },
      query: FilmToCallsheets,
      fetchPolicy: "cache-and-network",
      variables() {
        return this.filmToCallsheetsVariables;
      }
    }
  }
};
</script>
